import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { PoiDetailService } from './poi-detail.service';
import { PoiSelectedEventData } from '../PoiSelectedEventData';
import { AuthService, UserInfo } from './auth/auth.service';
import { FmsTreeComponent, LocationSelectedEvent } from './main/fms-tree.component';
import { SiteService } from './site-selector/site.service';
import { SettingsOverviewComponent } from './settings/settings-overview.component';
import { DefectAgendaService } from './settings/defect-agenda.service';
import { PoiInfoPanelComponent, FmsNodeInfo } from './poi-info-panel/poi-info-panel.component';
import { IvionNavigationService } from './api/ivion-navigation.service';
import { TranslatePipe } from './pipes/translate.pipe';
import { SearchComponent } from './search/search.component';
import { SearchResult } from './api/middleware-api.service';
import { FavoritePoiOverviewComponent } from './favorites/favorite-poi-overview.component';
import { ToastComponent } from './shared/toast.component';
import { DefectEditorComponent } from './defects/defect-editor/defect-editor.component';
import { DefectEventService } from './defects/defect-event.service';
import { ReportDefectEventData, IDefect } from './defects/defect.models';
import { DefectAgendaDialogComponent } from './defects/defect-agenda-dialog.component';
import { DefectOverviewComponent } from './defects/defect-overview/defect-overview.component';
import { DefectDetailComponent } from './defects/defect-detail/defect-detail.component';
import { DefectUserRoleService } from './defects/defect-user-role.service';
import { RoutingPanelComponent } from './routing/routing-panel.component';

// Import styles directly (webpack handles SCSS)
import './app.component.scss';

@Component({
	selector: 'app-root',
	standalone: true,
	imports: [CommonModule, FormsModule, RouterOutlet, FmsTreeComponent, SettingsOverviewComponent, PoiInfoPanelComponent, TranslatePipe, SearchComponent, FavoritePoiOverviewComponent, ToastComponent, DefectEditorComponent, DefectAgendaDialogComponent, DefectOverviewComponent, DefectDetailComponent, RoutingPanelComponent],
	template: `
		<!-- HEADER PANEL - only visible on map route -->
		<div id="headerPanel" *ngIf="isMapRoute">
			<div class="header-left-container">
				<img class="logo" alt="Logo Škoda" src="assets/images/skoda-logo.png">
			</div>

			<div class="menu-container" [class.active]="menuEnabled">
				<div class="menu-container-items">
					<div>
						<img src="assets/images/site-selection-icon-colored.png" />
						<a (click)="showSiteSelector()">{{ 'campus_menu_title' | translate }}</a>
					</div>
					<div *ngIf="showFindRouteMenu">
						<img src="assets/images/search-green.png" />
						<a (click)="searchRoute()">{{ 'find_route_menu_title' | translate }}</a>
					</div>
					<div>
						<img src="assets/images/place-green.png" />
						<a (click)="showDefectOverview()">{{ 'defects_menu_title' | translate }}</a>
					</div>
					<div *ngIf="showDeviceRevisionMenu">
						<img src="assets/images/facility_mngmt_maintenance.png" />
						<a (click)="showDeviceRevisionOverview()">{{ 'device_revision_menu_title' | translate }}</a>
					</div>
					<div>
						<img src="assets/images/star-outline-green.png" />
						<a (click)="openFavoritePoiOverview()">{{ 'favorite_poi_title' | translate }}</a>
					</div>
					<div>
						<img src="assets/images/settings.png" />
						<a (click)="openSettingsOverview()">{{ 'settings_menu_title' | translate }}</a>
					</div>
					<div *ngIf="!defectAgendaEnabled">
						<img src="assets/images/defect-agenda-disabled-green.png" />
						<a (click)="enableDefectAgenda()">{{ 'enable_defect_agenda_menu_title' | translate }}</a>
					</div>
					<div *ngIf="defectAgendaEnabled">
						<img src="assets/images/defect-agenda-enabled.png" />
						<a (click)="disableDefectAgenda()">{{ 'disable_defect_agenda_menu_title' | translate }}</a>
					</div>
					<div>
						<img src="assets/images/round_help_48dp.png" />
						<a [href]="'help_url' | translate" target="_blank">{{ 'help_menu_title' | translate }}</a>
					</div>
				</div>
			</div>

			<a class="menu-icon" (click)="showOrHideMenu()">
				<i class="fa fa-bars"></i>
			</a>

			<div class="header-right-container">
				<app-search (resultSelected)="onSearchResultSelected($event)"></app-search>
				<div (click)="logoutUser()" class="username">
					{{ userFullName }} <i class="fa fa-power-off"></i>
				</div>
			</div>
		</div>
		<!-- /HEADER PANEL -->

		<div class="content-container" [class.full-height]="!isMapRoute">
			<!-- Router outlet for lazy-loaded components -->
			<router-outlet></router-outlet>

			<!-- Map UI elements - only visible on map route -->
			<ng-container *ngIf="isMapRoute">
				<!-- TREEVIEW PANEL -->
				<div class="treeView-panel" *ngIf="treeViewVisible">
					<app-fms-tree (locationSelected)="onLocationSelected($event)"></app-fms-tree>
				</div>
				<!-- /TREEVIEW PANEL -->

				<!-- Center panel - Map container with toggle buttons -->
				<div class="map-container">
					<!-- Info panel toggle button -->
					<div class="info-panel-toggle-button" *ngIf="infoPanelModel" [class.panel-open]="infoPanelVisible">
						<button (click)="hideOrShowInfoPanel()">
							<span class="fa fa-info-circle" *ngIf="!infoPanelVisible"></span>
							<span class="chevron-icon" *ngIf="infoPanelVisible">&gt;</span>
						</button>
					</div>

					<!-- TreeView panel toggle button -->
					<div class="treeView-panel-toggle-button" [class.panel-open]="treeViewVisible">
						<button (click)="hideOrShowTreeViewPanel()">
							<img src="assets/images/baseline_account_tree_black_48dp.png" *ngIf="!treeViewVisible" />
							<span class="chevron-icon" *ngIf="treeViewVisible">&lt;</span>
						</button>
					</div>
				</div>

				<!-- INFO PANEL -->
				<div class="info-panel" *ngIf="infoPanelModel && infoPanelVisible && !routingVisible">
					<app-poi-info-panel
						*ngIf="selectedPoi || selectedFmsInfo"
						[poi]="selectedPoi"
						[fmsInfo]="selectedFmsInfo"
						(navigateEvent)="onPoiNavigate($event)"
						(viewDefectEvent)="onViewDefectFromPanel($event)"
						(editDefectEvent)="onEditDefectFromPanel($event)">
					</app-poi-info-panel>
				</div>

				<!-- ROUTING PANEL (right side panel, same position as info panel) -->
				<div class="info-panel routing-panel" *ngIf="routingVisible">
					<app-routing-panel
						(closeEvent)="closeRouting()">
					</app-routing-panel>
				</div>

				<!-- SCREEN CONTAINER (for overlays like defect overview, settings, etc.) -->
				<div class="screen" *ngIf="showScreenContainer">
					<!-- Settings Overview -->
					<app-settings-overview
						*ngIf="activeScreen === 'settings'"
						(closeSettings)="closeScreen()">
					</app-settings-overview>

					<!-- Favorite POI Overview -->
					<app-favorite-poi-overview
						*ngIf="activeScreen === 'favorites'"
						(closeEvent)="closeScreen()">
					</app-favorite-poi-overview>

					<!-- Defect Editor -->
					<app-defect-editor
						*ngIf="activeScreen === 'defectEditor'"
						[eventData]="defectEventData"
						[defectToEdit]="defectToEdit"
						(closeEvent)="closeScreen()"
						(defectCreated)="onDefectCreated($event)"
						(defectUpdated)="onDefectUpdated($event)">
					</app-defect-editor>

					<!-- Defect Overview -->
					<app-defect-overview
						*ngIf="activeScreen === 'defects'"
						(closeEvent)="closeScreen()"
						(viewDefect)="onViewDefect($event)"
						(editDefectEvent)="onEditDefectFromOverview($event)"
						(goToLocation)="onGoToDefectLocation($event)">
					</app-defect-overview>

					<!-- Defect Detail -->
					<app-defect-detail
						*ngIf="activeScreen === 'defectDetail'"
						[defect]="selectedDefect"
						(closeEvent)="closeDefectDetail()"
						(editDefectEvent)="onEditDefectFromDetail($event)">
					</app-defect-detail>

					<!-- Other screens (placeholder for future implementation) -->
					<div class="screen-modal-container" *ngIf="activeScreen !== 'settings' && activeScreen !== 'favorites' && activeScreen !== 'defectEditor' && activeScreen !== 'defects' && activeScreen !== 'defectDetail'">
						<a class="screen-close" (click)="closeScreen()"></a>
						<p style="color: white;">{{ activeScreen | titlecase }} - Coming soon</p>
					</div>
				</div>
			</ng-container>
		</div>

		<!-- Defect Agenda Dialog -->
		<app-defect-agenda-dialog
			*ngIf="showDefectAgendaDialog"
			(enableAgenda)="onEnableDefectAgenda()"
			(cancel)="onCancelDefectAgenda()">
		</app-defect-agenda-dialog>

		<!-- Toast notifications -->
		<app-toast></app-toast>
	`,
	styles: []  // Styles imported via webpack (see import above)
})
export class AppComponent implements OnInit, OnDestroy {
	// UI State
	menuEnabled = false;
	infoPanelVisible = true;
	treeViewVisible = false;
	showScreenContainer = false;
	showDeviceRevisionMenu = false;
	showFindRouteMenu = false;  // Find route menu entry is hidden for now
	isMapRoute = false;
	activeScreen: 'settings' | 'defects' | 'favorites' | 'deviceRevision' | 'defectEditor' | 'defectDetail' | '' = '';
	// Where to return when the defect detail is closed: the overview it was
	// opened from, or the map when it was opened from the POI info panel.
	defectDetailReturnTo: 'defects' | 'map' = 'defects';
	routingVisible = false;
	defectAgendaEnabled = false;  // Disabled by default
	defectEventData: ReportDefectEventData | null = null;
	// Defect passed to the editor when opened in edit mode; null means create mode
	defectToEdit: IDefect | null = null;
	showDefectAgendaDialog = false;
	selectedDefect: IDefect | null = null;

	// User info
	version = '2.0.0';
	userFullName = 'User';
	baseUrl = '';
	isAuthenticated = false;
	selectedSiteName: string | null = null;

	// Info panel model (will be replaced with proper service)
	infoPanelModel: any = null;
	selectedPoi: PoiSelectedEventData | null = null;
	// FMS tree node info shown in the panel for levels without a POI (sector/floor/room)
	selectedFmsInfo: FmsNodeInfo | null = null;

	/** Upper bound for how long the splash screen may cover the app. */
	private static readonly SPLASH_TIMEOUT_MS = 15000;

	private splashHidden = false;
	private splashTimeout: ReturnType<typeof setTimeout> | null = null;

	private poiSubscription: Subscription | null = null;
	private authSubscription: Subscription | null = null;
	private routerSubscription: Subscription | null = null;
	private siteSubscription: Subscription | null = null;
	private defectAgendaSubscription: Subscription | null = null;
	private reportDefectSubscription: Subscription | null = null;
	private viewDefectSubscription: Subscription | null = null;
	private showDialogSubscription: Subscription | null = null;
	private cdr = inject(ChangeDetectorRef);
	private authService = inject(AuthService);
	private router = inject(Router);
	private siteService = inject(SiteService);
	private defectAgendaService = inject(DefectAgendaService);
	private poiDetailService = inject(PoiDetailService);
	private ivionNavigation = inject(IvionNavigationService);
	private defectEventService = inject(DefectEventService);
	private defectUserRoleService = inject(DefectUserRoleService);

	ngOnInit(): void {
		this.baseUrl = window.location.origin;

		// Check initial route (use startsWith to handle query params)
		this.isMapRoute = this.router.url.startsWith('/map');

		// Subscribe to route changes
		this.routerSubscription = this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd)
		).subscribe((event) => {
			this.isMapRoute = event.urlAfterRedirects.startsWith('/map');
			// The routed component is in place now, so the splash screen has
			// something to hand over to (the map has its own loading overlay).
			this.hideSplashScreen();
			this.cdr.detectChanges();
		});

		// Subscribe to site selection
		this.siteSubscription = this.siteService.selectedSite$.subscribe(site => {
			this.selectedSiteName = site?.name ?? null;
			this.cdr.detectChanges();
		});

		// Subscribe to authentication state
		this.authSubscription = this.authService.userData$.subscribe((userData: UserInfo | null) => {
			if (userData) {
				this.isAuthenticated = true;
				this.userFullName = userData.name || userData.preferred_username || userData.email || 'User';

				// Load the defect role info as soon as we are authenticated. It used to
				// be loaded only by the defect overview, so reaching a defect straight
				// from the map left it null and every edit permission check failed.
				this.defectUserRoleService.loadUserInfo().subscribe();
			} else {
				this.isAuthenticated = false;
				this.userFullName = 'User';
			}
			this.cdr.detectChanges();
		});

		// Subscribe to POI selection events
		this.poiSubscription = this.poiDetailService.poiSelected$.subscribe(data => {
			this.selectedPoi = data;
			this.selectedFmsInfo = null;
			this.infoPanelModel = { type: 'poi', data };
			this.infoPanelVisible = true;
			document.body.classList.add('info-panel-open');
			this.cdr.detectChanges();
		});

		// Subscribe to defect agenda state
		this.defectAgendaSubscription = this.defectAgendaService.defectAgendaEnabled$.subscribe(enabled => {
			this.defectAgendaEnabled = enabled;
			this.cdr.detectChanges();
		});

		// Subscribe to show dialog events
		this.showDialogSubscription = this.defectAgendaService.showDialog$.subscribe(() => {
			this.showDefectAgendaDialog = true;
			this.cdr.detectChanges();
		});

		// Subscribe to report defect events from context menu
		this.reportDefectSubscription = this.defectEventService.reportDefect$.subscribe(data => {
			console.log('[AppComponent] Report defect event received:', data);
			// Check if defect agenda is enabled, show dialog if not
			this.defectAgendaService.checkDefectAgenda()
				.then(() => {
					this.openDefectEditor(data);
				})
				.catch(() => {
					// User cancelled - do nothing
					console.log('[AppComponent] Defect agenda enable cancelled');
				});
		});

		// Subscribe to view defect requests (deep link /defect/{id})
		this.viewDefectSubscription = this.defectEventService.viewDefect$.subscribe(defect => {
			this.openDefectFromDeepLink(defect);
		});

		// The splash screen stays up until the first route is rendered (legacy hid
		// it once app settings had loaded). A navigation that never completes -
		// the auth guard redirecting to Keycloak emits NavigationCancel, not
		// NavigationEnd - must not leave it up forever, hence the safety net.
		this.splashTimeout = setTimeout(() => this.hideSplashScreen(), AppComponent.SPLASH_TIMEOUT_MS);
	}

	ngOnDestroy(): void {
		document.body.classList.remove('info-panel-open');
		this.clearSplashTimeout();
		this.poiSubscription?.unsubscribe();
		this.authSubscription?.unsubscribe();
		this.routerSubscription?.unsubscribe();
		this.siteSubscription?.unsubscribe();
		this.defectAgendaSubscription?.unsubscribe();
		this.reportDefectSubscription?.unsubscribe();
		this.viewDefectSubscription?.unsubscribe();
		this.showDialogSubscription?.unsubscribe();
	}

	private hideSplashScreen(): void {
		if (this.splashHidden) {
			return;
		}

		this.splashHidden = true;
		this.clearSplashTimeout();

		const splashScreen = document.querySelector('.splashscreen-loader-container');
		if (splashScreen) {
			splashScreen.classList.add('inactive');
		}
	}

	private clearSplashTimeout(): void {
		if (this.splashTimeout !== null) {
			clearTimeout(this.splashTimeout);
			this.splashTimeout = null;
		}
	}

	// Menu actions
	showOrHideMenu(): void {
		this.menuEnabled = !this.menuEnabled;
	}

	hideMenu(): void {
		this.menuEnabled = false;
	}

	// Panel visibility
	hideOrShowInfoPanel(): void {
		this.infoPanelVisible = !this.infoPanelVisible;
		document.body.classList.toggle('info-panel-open', this.infoPanelVisible);
		window.dispatchEvent(new Event('resize'));
	}

	hideOrShowTreeViewPanel(): void {
		this.treeViewVisible = !this.treeViewVisible;
		window.dispatchEvent(new Event('resize'));
	}

	onLocationSelected(event: LocationSelectedEvent): void {
		console.log('[AppComponent] Location selected:', event);

		// Building and FunctionalArea are handled by the FMS tree itself via
		// PoiDetailService (POI navigation + info panel). The remaining levels
		// have no POI, so show the plain FMS info panel here (legacy behavior).
		if (event.type !== 'sector' && event.type !== 'floor' && event.type !== 'room') {
			return;
		}

		this.selectedFmsInfo = {
			type: event.type,
			id: event.id,
			name: event.name,
			description: event.description,
			area: event.area,
		};
		this.selectedPoi = null;
		this.infoPanelModel = { type: 'fms', data: this.selectedFmsInfo };
		this.infoPanelVisible = true;
		document.body.classList.add('info-panel-open');
		this.cdr.detectChanges();
	}

	// Navigation actions
	showSiteSelector(): void {
		this.hideMenu();
		this.siteService.clearSite();
		this.router.navigate(['/']);
	}

	searchRoute(): void {
		this.routingVisible = true;
		this.showScreenContainer = false;
		this.activeScreen = '';
		this.hideMenu();
	}

	closeRouting(): void {
		this.routingVisible = false;
	}

	showDefectOverview(): void {
		this.routingVisible = false;
		this.activeScreen = 'defects';
		this.showScreenContainer = true;
		this.hideMenu();
	}

	showDeviceRevisionOverview(): void {
		this.routingVisible = false;
		this.activeScreen = 'deviceRevision';
		this.showScreenContainer = true;
		this.hideMenu();
	}

	openFavoritePoiOverview(): void {
		this.routingVisible = false;
		this.activeScreen = 'favorites';
		this.showScreenContainer = true;
		this.hideMenu();
	}

	openSettingsOverview(): void {
		this.routingVisible = false;
		this.activeScreen = 'settings';
		this.showScreenContainer = true;
		this.hideMenu();
	}

	closeScreen(): void {
		this.showScreenContainer = false;
		this.activeScreen = '';
		this.defectToEdit = null;
	}

	enableDefectAgenda(): void {
		this.defectAgendaService.enableDefectAgenda();
		this.hideMenu();
	}

	disableDefectAgenda(): void {
		this.defectAgendaService.disableDefectAgenda();
		this.hideMenu();
	}

	// Defect agenda dialog handlers
	onEnableDefectAgenda(): void {
		this.showDefectAgendaDialog = false;
		this.defectAgendaService.handleDialogResult(true);
		this.cdr.detectChanges();
	}

	onCancelDefectAgenda(): void {
		this.showDefectAgendaDialog = false;
		this.defectAgendaService.handleDialogResult(false);
		this.cdr.detectChanges();
	}

	logoutUser(): void {
		this.authService.logout();
	}

	onPoiNavigate(poi: PoiSelectedEventData): void {
		console.log('[AppComponent] Navigate to POI:', poi);
		// Navigation is handled by PoiInfoPanelComponent using IVION API
	}

	// Defect info panel (map) event handlers
	onViewDefectFromPanel(defect: IDefect): void {
		console.log('[AppComponent] View defect from info panel:', defect);
		this.selectedDefect = defect;
		this.defectDetailReturnTo = 'map';
		this.activeScreen = 'defectDetail';
		this.showScreenContainer = true;
		this.cdr.detectChanges();
	}

	onEditDefectFromPanel(defect: IDefect): void {
		console.log('[AppComponent] Edit defect from info panel:', defect);
		this.defectToEdit = defect;
		this.defectEventData = null;
		this.selectedDefect = null;
		this.activeScreen = 'defectEditor';
		this.showScreenContainer = true;
		this.cdr.detectChanges();
	}

	onSearchResultSelected(result: SearchResult): void {
		console.log('[AppComponent] Search result selected:', result);
		// Navigation and info panel update handled by SearchComponent
	}

	// Defect editor methods
	openDefectEditor(eventData: ReportDefectEventData): void {
		this.defectEventData = eventData;
		this.defectToEdit = null;
		this.activeScreen = 'defectEditor';
		this.showScreenContainer = true;
		this.cdr.detectChanges();
	}

	onDefectCreated(defect: IDefect): void {
		console.log('[AppComponent] Defect created:', defect);
		// TODO: Navigate to POI if needed
	}

	onDefectUpdated(defect: IDefect): void {
		console.log('[AppComponent] Defect updated:', defect);
	}

	// Defect overview event handlers
	onViewDefect(defect: IDefect): void {
		console.log('[AppComponent] View defect:', defect);
		this.selectedDefect = defect;
		this.defectDetailReturnTo = 'defects';
		this.activeScreen = 'defectDetail';
		this.cdr.detectChanges();
	}

	/**
	 * Opens a defect coming from a deep link (/defect/{id}). Legacy
	 * DefectService.openDefectDetailWithId opened the defect overview first and
	 * the detail on top of it, so closing the detail returns to the overview.
	 */
	private openDefectFromDeepLink(defect: IDefect): void {
		this.showDefectOverview();
		this.onViewDefect(defect);
	}

	onEditDefectFromOverview(defect: IDefect): void {
		console.log('[AppComponent] Edit defect from overview:', defect);
		this.defectToEdit = defect;
		this.defectEventData = null;
		// Close overview and open editor with existing defect
		this.activeScreen = 'defectEditor';
		this.cdr.detectChanges();
	}

	async onGoToDefectLocation(defect: IDefect): Promise<void> {
		console.log('[AppComponent] Go to defect location:', defect);
		this.closeScreen();
		// Same sequence as the legacy DefectOverView.goToDefectLocation: defect POI
		// types are hidden while the agenda is off, so enable it first, otherwise
		// the navigated-to POI is not rendered and cannot be highlighted.
		this.defectAgendaService.enableDefectAgenda();

		if (defect.poiId && defect.poiId > 0) {
			// Navigate + highlight and open the defect info panel for the POI
			const poi = await this.poiDetailService.selectPoi(defect.poiId);
			if (poi) {
				return;
			}
			console.warn('[AppComponent] POI not found for defect, falling back to its location:', defect.poiId);
		}

		// No (resolvable) POI: at least move the view to the stored defect location
		// (legacy: IvService.navigateToLocation). There is nothing to highlight here.
		const location = defect.location;
		if (location && (location.lon !== 0 || location.lat !== 0)) {
			await this.ivionNavigation.navigateToLocation(location);
		} else {
			console.warn('[AppComponent] Defect has no POI and no location:', defect.id);
		}
	}

	// Defect detail event handlers
	closeDefectDetail(): void {
		this.selectedDefect = null;

		if (this.defectDetailReturnTo === 'map') {
			// Opened from the POI info panel: close the overlay and go back to
			// the map, where the info panel is still open.
			this.closeScreen();
		} else {
			this.activeScreen = 'defects';
		}

		this.cdr.detectChanges();
	}

	onEditDefectFromDetail(defect: IDefect): void {
		console.log('[AppComponent] Edit defect from detail:', defect);
		this.defectToEdit = defect;
		this.defectEventData = null;
		this.selectedDefect = null;
		this.activeScreen = 'defectEditor';
		this.cdr.detectChanges();
	}
}
